import * as stylex from "@stylexjs/stylex";
import type { LucideIcon } from "lucide-react";
import type { WeatherIcon } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { ColorBlock } from "./ColorBlock";
import { Eyebrow } from "./Eyebrow";
import { Icon } from "./Icon";
import { weatherIcons } from "./weatherIcons";
export type WeatherNowTone = 1 | 2 | 3 | 4 | "soft" | "deep";
export type WeatherDetail = {
  label: string;
  value: string;
  icon?: LucideIcon;
};
export type WeatherNowLabels = {
  high: string;
  low: string;
  feelsLike: string;
};
type WeatherNowProps = {
  location: string;
  temp: number;
  condition: string;
  icon: WeatherIcon;
  high?: number;
  low?: number;
  feelsLike?: number;
  details?: readonly WeatherDetail[];
  labels: WeatherNowLabels;
  tone?: WeatherNowTone;
  grid?: boolean;
  xstyle?: stylex.StyleXStyles;
};
const _temp = {
  k1xSpc: "xrvj5dj",
  kumcoG: "xqketvx",
  kOIVth: "xddi9bj",
  kLKAdn: "x11xfbwc",
  kEafiO: "x178xt8z",
  kPef9Z: "x13fuv20",
  kLZC3w: "x1ngkdmg",
  "$$css": true
};
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    $$css: true
  },
  details: (count: number) => [_temp, {
    "--x-gridTemplateColumns": `repeat(${count}, minmax(0,1fr))` != null ? `repeat(${count}, minmax(0,1fr))` : undefined
  }],
  detailsGrid: {
    kumcoG: "xnby9oq",
    khm7nJ: "xj7a29x",
    kLZC3w: "x193cl7z",
    $$css: true
  },
  detailsOnDeep: {
    kLZC3w: "xe2x8ty",
    $$css: true
  },
  detailLabel: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x167g77z",
    $$css: true
  }
};
export function WeatherNow({
  location,
  temp,
  condition,
  icon,
  high,
  low,
  feelsLike,
  details = [],
  labels,
  tone = 3,
  grid = false,
  xstyle
}: WeatherNowProps) {
  const range = rangeText({
    high,
    low,
    feelsLike
  }, labels);
  return <ColorBlock tone={tone} xstyle={[styles.root, xstyle]}>
      <Eyebrow>{location}</Eyebrow>
      <div className="x78zum5 x6s0dn4 xf5y8le">
        <div className="xj5wx7v xl9ayud xgeghw x63rib xss6m8b">{temp}°</div>
        <Icon icon={weatherIcons[icon]} size={112} strokeWidth={1.25} />
      </div>
      <div className="x78zum5 xdt5ytf xt4j9g6">
        <div className="x1tgwohi x182gq9d xsast7f xcwzsbu">{condition}</div>
        {range && <div className="x12feioo x1cf7cre">{range}</div>}
      </div>
      {details.length > 0 && <div {...stylex.props(styles.details(details.length), grid && styles.detailsGrid, tone === "deep" && styles.detailsOnDeep)}>
          {details.map(detail => <div key={detail.label} className="x78zum5 xdt5ytf x152posx">
              <Eyebrow size="caption" xstyle={styles.detailLabel}>
                {detail.icon && <Icon icon={detail.icon} size={16} />}
                {detail.label}
              </Eyebrow>
              <div {...{
          0: {
            className: "xneq65o x1cf7cre xrg9stw"
          },
          1: {
            className: "xneq65o x1cf7cre xrg9stw x1159mfc"
          }
        }[!!grid << 0]}>
                {detail.value}
              </div>
            </div>)}
        </div>}
    </ColorBlock>;
}
function rangeText({
  high,
  low,
  feelsLike
}: Pick<WeatherNowProps, "high" | "low" | "feelsLike">, labels: WeatherNowLabels) {
  const parts = [high !== undefined && `${labels.high} ${high}°`, low !== undefined && `${labels.low} ${low}°`, feelsLike !== undefined && `${labels.feelsLike} ${feelsLike}°`].filter(Boolean);
  return parts.join(" · ");
}